import {
  AUDIENCE_FIELD_DEFINITIONS,
  AUDIENCE_FIELD_KEYS,
  BRAND_FIELD_DEFINITIONS,
  BRAND_FIELD_KEYS,
  BRAND_LIMITS,
  BRAND_SUGGESTION_BASES,
  BRAND_SUGGESTION_LIST_MAX,
  VALUE_PHILOSOPHIES,
  canonicalSources,
  checkBrandFieldValue,
  emptyBrandAudienceValues,
  isBrandValueEmpty,
  normalizeBrandList,
  normalizeBrandText,
  type BrandAudienceValues,
  type BrandFieldKey,
  type BrandFieldValues,
  type BrandListFieldKey,
  type BrandSuggestion,
  type BrandSuggestionProvenance,
  type BrandSuggestionRequest,
  type BrandValueProvenance,
} from '@mg/shared';
import { isEmptyValue, type BrandDraft, type DraftAudience, type DraftField, type FieldValue } from '../draft/brandDraft';

/**
 * AI suggestions in Brand Setup (spec:113-125; web.md §5.8; final-design §9, lane W6), the rules without
 * the screen: which fields to ask for, what the request carries, and which answers may be offered. Pure.
 *
 * A suggestion is only ever an OFFER: nothing here writes the draft. The customer's Use suggestion fills
 * the field (SuggestionsProvider), and the step save stores it.
 */

/** A list or a text value for one field, as the request carries it; null = left out. */
type RequestValue = string | string[] | null;

const isListKey = (key: BrandFieldKey): key is BrandListFieldKey => {
  const kind = BRAND_FIELD_DEFINITIONS[key].kind;
  return kind === 'tags' || kind === 'guidelines';
};

/**
 * The fields to ask a suggestion for (spec:117): of the fields the step shows, those that are empty and
 * that the customer has not touched (a field they cleared is theirs: an emptied field is not asked for),
 * leaving out every field `settled` names (asked already, or dismissed). In field order.
 */
export function keysToAsk(
  state: BrandDraft,
  shown: Iterable<BrandFieldKey>,
  settled: (key: BrandFieldKey) => boolean,
): BrandFieldKey[] {
  const onScreen = new Set(shown);
  return BRAND_FIELD_KEYS.filter((key) => {
    if (!onScreen.has(key) || settled(key)) return false;
    const field = state.fields[key];
    return !field.touched && isEmptyValue(field.value);
  });
}

/**
 * One field's value as the request carries it: what the step could save. Text as stored (trimmed, line
 * breaks as LF); a list normalized, without items past its per-item limit (grandfathered hashtags and
 * catchphrases, E4) and at most its item limit; Value philosophy only as one of the four. Empty, or text
 * over its limit (the step refuses to save it): left out.
 */
function requestValue(key: BrandFieldKey, value: FieldValue): RequestValue {
  const def = BRAND_FIELD_DEFINITIONS[key];
  if (isListKey(key)) {
    if (!Array.isArray(value)) return null;
    const items = normalizeBrandList(key, value)
      .filter((item) => item.length <= def.maxLength)
      .slice(0, def.maxItems ?? 0);
    return items.length > 0 ? items : null;
  }
  if (def.kind === 'valuePhilosophy') {
    return typeof value === 'string' && (VALUE_PHILOSOPHIES as readonly string[]).includes(value) ? value : null;
  }
  const text = typeof value === 'string' ? normalizeBrandText(value) : null;
  return text !== null && text.length <= def.maxLength ? text : null;
}

/** A field's tags as the wizard holds them. */
const provenanceOf = (f: DraftField): BrandValueProvenance => ({ sources: canonicalSources(f.sources), aiSuggested: f.aiSuggested });

type RequestAudience = { values: BrandAudienceValues; provenance: BrandSuggestionProvenance['audiences'][number] };

/** An audience card as the request carries it, with the tags of what it carries, or null when nothing in it could be saved. */
function requestAudience(card: DraftAudience): RequestAudience | null {
  const values = emptyBrandAudienceValues();
  const provenance: RequestAudience['provenance'] = {};
  let any = false;
  for (const key of AUDIENCE_FIELD_KEYS) {
    const raw = card.fields[key].value;
    const text = typeof raw === 'string' ? normalizeBrandText(raw) : null;
    if (text !== null && text.length <= AUDIENCE_FIELD_DEFINITIONS[key].maxLength) {
      values[key] = text;
      provenance[key] = provenanceOf(card.fields[key]);
      any = true;
    }
  }
  return any ? { values, provenance } : null;
}

/**
 * The body of `POST /tenants/:id/brand/suggestions` (BrandSuggestionRequest): what the wizard holds NOW
 * for every field, saved or not (typed, changed, copied, imported or an earlier suggestion used), so
 * text entered in earlier steps feeds the suggestions of later ones (spec:119); the audience cards the same
 * way; each value's tags, because a value holding a web page's text must never feed Writing guidelines or
 * Never say, and only what the customer typed or changed counts as entered (O13, spec:119: the server
 * decides); and the empty fields to suggest for, each once. Values are within BRAND_LIMITS; empty ones are
 * left out.
 */
export function suggestionRequest(state: BrandDraft, keys: readonly BrandFieldKey[]): BrandSuggestionRequest {
  const draft: Partial<Record<BrandFieldKey, RequestValue>> = {};
  const fields: BrandSuggestionProvenance['fields'] = {};
  for (const key of BRAND_FIELD_KEYS) {
    const value = requestValue(key, state.fields[key].value);
    if (value === null) continue;
    draft[key] = value;
    fields[key] = provenanceOf(state.fields[key]);
  }
  const cards = state.audiences
    .map(requestAudience)
    .filter((card): card is RequestAudience => card !== null)
    .slice(0, BRAND_LIMITS.audiences);
  const asked = new Set(keys);
  const request: BrandSuggestionRequest = {
    draft: draft as Partial<BrandFieldValues>,
    provenance: { fields, audiences: cards.map((card) => card.provenance) },
    keys: BRAND_FIELD_KEYS.filter((key) => asked.has(key)),
  };
  if (cards.length > 0) request.audiences = cards.map((card) => card.values);
  return request;
}

/**
 * A suggested value the field can take, or null: text as stored; a tag list or guideline list of 1 to
 * BRAND_SUGGESTION_LIST_MAX items, normalized as a typed entry would be (a hashtag gets its `#`); Value
 * philosophy one of the four options (spec:123). Within the field's limits (checkBrandFieldValue, the
 * same check the save runs), never empty.
 */
function offeredValue(key: BrandFieldKey, raw: unknown): BrandSuggestion['value'] | null {
  let value: string | string[] | null;
  if (isListKey(key)) {
    if (!Array.isArray(raw) || raw.some((item) => typeof item !== 'string')) return null;
    value = normalizeBrandList(key, raw).slice(0, BRAND_SUGGESTION_LIST_MAX);
  } else if (BRAND_FIELD_DEFINITIONS[key].kind === 'valuePhilosophy') {
    value = typeof raw === 'string' ? raw : null;
  } else {
    value = typeof raw === 'string' ? normalizeBrandText(raw) : null;
  }
  if (value === null || isBrandValueEmpty(value)) return null;
  return checkBrandFieldValue(key, value).length === 0 ? (value as BrandSuggestion['value']) : null;
}

const isBasis = (basis: unknown): basis is BrandSuggestion['basis'] =>
  typeof basis === 'string' && (BRAND_SUGGESTION_BASES as readonly string[]).includes(basis);

/**
 * The answers that may be offered: one per field asked for (the first), with a basis the box can name
 * (spec:123) and a value the field can take (offeredValue). Anything else in the answer is dropped, so the
 * box never shows, and Use suggestion never fills in, a value the step could not save.
 */
export function offeredSuggestions(response: unknown, asked: readonly BrandFieldKey[]): BrandSuggestion[] {
  const open = new Set<string>(asked);
  const answers = (response as { suggestions?: unknown } | null | undefined)?.suggestions;
  if (!Array.isArray(answers)) return [];
  const out: BrandSuggestion[] = [];
  for (const answer of answers as unknown[]) {
    if (answer === null || typeof answer !== 'object') continue;
    const { key, value: raw, basis } = answer as { key?: unknown; value?: unknown; basis?: unknown };
    if (typeof key !== 'string' || !open.has(key)) continue;
    open.delete(key);
    if (!isBasis(basis)) continue;
    const value = offeredValue(key as BrandFieldKey, raw);
    if (value === null) continue;
    out.push({ key, value, basis } as BrandSuggestion);
  }
  return out;
}

/** A suggested value copied, so the draft never shares an array with the offer. */
export function copySuggestedValue(value: BrandSuggestion['value']): string | string[] {
  return Array.isArray(value) ? [...value] : value;
}
